import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Actions from '../Actions';
import Button from '../Button';
import Empty from '../Empty';
import FontAwesomeIcon from '../Icon';
import Table, { Hidden } from '../Table';
import LoadState from 'src/components/LoadState/LoadState';
import Skeleton from 'src/components/Skeleton/Skeleton';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import AnnouncementForm from '../AnnouncementForm';
import ConfirmationModal from 'src/components/ConfirmationModal/ConfirmationModal';
import { Link } from 'react-router-dom';
import { PATHS } from 'src/common/constants';
import { convertAnyTimestampToLocal } from 'src/helpers/validationHelper';
import Search from 'src/components/Search/Search';
import { useTablePagination } from 'src/SharedCustomHooks/UseTablePagination/useTablePagination';

const SKELETON_ROW_COUNT: number = 13;

const AnnouncementsTable = ({
  announcements,
  isLoadingAnnouncements = false,
  searchTerm = '',
  onSearch,
  onDeleteAnnouncement,
  onSaved,
}: any) => {
  const [showOverlay, setShowOverlay] = useState(false);
  const [announcementToDelete, setAnnouncementToDelete] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { itemsPerPage, onItemsPerPageChange } = useTablePagination();

  const user = useSelector(selectAuthenticatedUser);

  const canAddAnnouncement =
    user &&
    (user.roles[0].role.name === 'Admin' ||
      user.roles[0].role.name === 'Super Admin' ||
      user.roles[0].role.name === 'Property Developer' ||
      user.roles[0].role.name === 'Strata Manager');

  const canAddAnnouncementEmpty =
    user &&
    (user.roles[0].role.name === 'Admin' ||
      user.roles[0].role.name === 'Super Admin' ||
      user.roles[0].role.name === 'Property Developer');

  const handleAddAnnouncementClick = () => {
    setShowOverlay(true);
  };

  const handleAddAnnouncementFromEmpty = () => {
    setShowOverlay(true);
  };

  const handleOverlayClose = () => {
    setShowOverlay(false);
  };

  const handleDeleteAnnouncement = (data) => {
    setAnnouncementToDelete(data);
  };

  const cancelDeleteAnnouncement = () => {
    setAnnouncementToDelete(null);
  };

  const confirmDeleteAnnouncement = async () => {
    setIsDeleting(true);

    await onDeleteAnnouncement(announcementToDelete);

    setIsDeleting(false);
    setAnnouncementToDelete(null);
  };

  const isEmpty: boolean = !isLoadingAnnouncements && announcements.length === 0;

  const tableColumns: any[] = [
    {
      label: 'Title',
      accessor: 'title',
      sortable: true,
      cell: (data: any) => (
        <Link to={PATHS.ANNOUNCEMENTS_VIEW.replace(':id', data.id)}>
          {data.title}
        </Link>
      ),
    },
    {
      label: 'Property',
      accessor: 'propertyName',
    },
    {
      label: 'Announcement Date',
      cell: (data: any) =>
        convertAnyTimestampToLocal(data.announcementDate),
    },
    {
      cell: (data: any) =>
        user.roles[0].role.name !== 'Realtor' && (
          <Hidden>
            {user.roles[0].role.name === 'Home Owner' ? null : (
              <Button
                color="semantic"
                onClick={() => handleDeleteAnnouncement(data)}
                label={<FontAwesomeIcon glyph="trash" weight="light" />}
              />
            )}
          </Hidden>
        ),
    },
  ];

  const skeletonColumns: any[] = tableColumns.map((c: any) => ({
    ...c,
    cell: () => <Skeleton variant="text" width="70%" />,
  }));

  const skeletonData: any[] = Array.from(
    { length: SKELETON_ROW_COUNT },
    (_: unknown, i: number) => ({ id: `announcements-skeleton-${i}` })
  );

  const emptyNode: React.ReactNode = (
    <Empty
      direction="ttb"
      headline="No Announcements yet"
      strapline="All Announcements you add will be listed here so it's easier for you to manage."
      searchIsActive={searchTerm.trim()}
      cta={
        canAddAnnouncementEmpty ? (
          <Button
            color="teal"
            label="Add Announcement"
            onClick={handleAddAnnouncementFromEmpty}
            icon={
              <FontAwesomeIcon
                glyph="building"
                weight="light"
                size="small"
              />
            }
            size="small"
          />
        ) : null
      }
      image={
        <img
          alt="No Announcements yet"
          src="/assets/empty/properties.svg"
        />
      }
    />
  );

  return (
    <>
      <Actions compact>
        <Search
          size="small"
          placeholder="Search Announcements..."
          onChange={onSearch}
          theme={undefined}
        />
        {canAddAnnouncement && (
          <Button
            color="teal"
            label="Add Announcement"
            onClick={handleAddAnnouncementClick}
            icon={
              <FontAwesomeIcon
                glyph="building"
                weight="light"
                size="small"
              />
            }
            size="small"
          />
        )}
      </Actions>
      <LoadState
        isLoading={false}
        isEmpty={isEmpty}
        emptyNode={emptyNode}
      >
        <Table
          columns={isLoadingAnnouncements ? skeletonColumns : tableColumns}
          data={isLoadingAnnouncements ? skeletonData : announcements}
          paginate={{ itemsPerPage, position: 'both' }}
          paginationSkeleton={isLoadingAnnouncements}
          onItemsPerPageChange={onItemsPerPageChange}
        />
      </LoadState>
      {showOverlay && (
        <AnnouncementForm onClose={handleOverlayClose} onSaved={onSaved} />
      )}
      {announcementToDelete && (
        <ConfirmationModal
          title="Delete announcement"
          message={
            <>
              Are you sure you want to delete{' '}
              <strong>{announcementToDelete.title}</strong>?
            </>
          }
          warningMessage="This deletes the announcement and removes it from the Announcements tab on recipients' dashboards. It won't unsend any emails already delivered when it was created. Deleting this announcement can't be undone."
          variant="danger"
          confirmLabel="Delete"
          requireTypeToConfirm
          confirmationWord="DELETE"
          isSubmitting={isDeleting}
          onConfirm={confirmDeleteAnnouncement}
          onCancel={cancelDeleteAnnouncement}
        />
      )}
    </>
  );
};

export default AnnouncementsTable;
